import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, Mail, Check } from 'lucide-react'
import { Button, Input, Card, CardContent } from '@/components/ui'
import { supabase } from '@/lib/supabase'

interface FormState {
  name: string
  email: string
  message: string
}

interface SubmitResult {
  ok: boolean
  message?: string
  error?: string
}

/**
 * RequestAccess - Public page for requesting beta access
 *
 * Users can submit their name, email, and an optional message.
 * Submissions are stored in the beta_requests table and admin is notified.
 */
export function RequestAccess() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState<SubmitResult | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!form.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!form.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validate()) return

    setIsSubmitting(true)
    setResult(null)

    try {
      // Submit to database via RPC
      const { data, error } = await supabase.rpc('submit_beta_request', {
        p_email: form.email,
        p_name: form.name,
        p_message: form.message || null,
      })

      if (error) {
        setResult({ ok: false, error: error.message })
        return
      }

      const response = data as SubmitResult
      setResult(response)

      // If successful, also trigger the notification API
      if (response.ok) {
        // Fire and forget - don't block on notification
        fetch('/api/submit-beta-request', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            message: form.message,
          }),
        }).catch(() => {
          // Silently ignore notification failures
        })

        // Clear form on success
        setForm({ name: '', email: '', message: '' })
      }
    } catch {
      setResult({ ok: false, error: 'Something went wrong. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)] flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Link to="/" className="flex items-center gap-3 w-fit">
          <Sparkles className="w-8 h-8 text-[var(--color-kids-primary)]" />
          <span className="font-display text-2xl font-bold text-[var(--color-kids-text)]">
            Inkwell Jr.
          </span>
        </Link>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Success State */}
          {result?.ok ? (
            <>
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="font-display text-[var(--font-size-kid-2xl)] font-bold text-[var(--color-kids-text)] mb-2">
                  Request Submitted!
                </h1>
                <p className="text-[var(--color-kids-text-muted)]">
                  {result.message}
                </p>
              </div>

              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-[var(--color-kids-text-muted)] mb-6">
                    We'll review your request and send you an email if approved.
                  </p>
                  <Link to="/login">
                    <Button className="w-full" size="lg">
                      Back to Login
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Form State */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-[var(--color-kids-primary)]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-10 h-10 text-[var(--color-kids-primary)]" />
                </div>
                <h1 className="font-display text-[var(--font-size-kid-2xl)] font-bold text-[var(--color-kids-text)] mb-2">
                  Request Beta Access
                </h1>
                <p className="text-[var(--color-kids-text-muted)]">
                  Inkwell Jr is in private beta. Fill out the form below and we'll review your request.
                </p>
              </div>

              <Card>
                <CardContent className="p-8">
                  {/* Error Message */}
                  {result?.error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {result.error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <Input
                      label="Name"
                      type="text"
                      name="name"
                      placeholder="Your name"
                      value={form.name}
                      onChange={handleChange}
                      error={errors.name}
                      required
                      autoFocus
                    />

                    <Input
                      label="Email"
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={handleChange}
                      error={errors.email}
                      required
                    />

                    <div>
                      <label className="block text-sm font-medium text-[var(--color-kids-text)] mb-2">
                        Why do you want to try Inkwell Jr? (optional)
                      </label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border-2 border-[var(--color-kids-border)] bg-[var(--color-kids-surface)] text-[var(--color-kids-text)] placeholder-[var(--color-kids-text-muted)] focus:border-[var(--color-kids-primary)] focus:ring-0 focus:outline-none transition-colors resize-none"
                        placeholder="Tell us about your young writers..."
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      size="lg"
                      isLoading={isSubmitting}
                    >
                      Request Access
                    </Button>
                  </form>

                  <div className="mt-6 pt-6 border-t border-[var(--color-kids-border)]">
                    <p className="text-center text-[var(--color-kids-text-muted)]">
                      Already have an account?{' '}
                      <Link
                        to="/login"
                        className="text-[var(--color-kids-primary)] font-semibold hover:underline"
                      >
                        Log in
                      </Link>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
